import type {
  AgentRuntimeActionAvailabilityMap,
  AgentRuntimeInteractionRequest,
  AgentRuntimeSnapshot,
  AgentRuntimeStatus,
  AgentType,
} from "@cord/shared"

/**
 * Runtime-authority state machine for a thread entry.
 *
 * - `empty`: nothing restorable is cached in memory for this entry.
 * - `optimistic_seed`: client-side fork seed; useful for UX, but not authoritative.
 * - `control_plane_only`: runtime shell created from status/control-plane data without transcript truth.
 * - `authoritative_partial`: derived from real sync data, but catchup/baseline may still be incomplete; safe
 *   only as a forced best-effort display fallback.
 * - `authoritative`: transcript state that can stay visible across reconnects. The current connection still re-binds
 *   authority via `runtimeConnectionVersion` / `continuityValidatedConnectionVersion` before we treat it as a
 *   normal continuity-validated replica source again.
 */
export type ThreadRuntimeMaterialization =
  | "empty"
  | "optimistic_seed"
  | "control_plane_only"
  | "authoritative_partial"
  | "authoritative"

export interface ThreadControlPlaneRuntimeSnapshotLike {
  agentType: AgentType
  pendingInteractions: AgentRuntimeInteractionRequest[]
  status: AgentRuntimeStatus
  conversationEpoch: number
}

export function isNonAuthoritativeRuntimeMaterialization(
  materialization: ThreadRuntimeMaterialization,
): materialization is Extract<
  ThreadRuntimeMaterialization,
  "optimistic_seed" | "control_plane_only"
> {
  return (
    materialization === "optimistic_seed" ||
    materialization === "control_plane_only"
  )
}

export function isDisplayOnlyRuntimeMaterialization(
  materialization: ThreadRuntimeMaterialization,
): materialization is Extract<ThreadRuntimeMaterialization, "optimistic_seed"> {
  return materialization === "optimistic_seed"
}

export function isSyncDerivedRuntimeMaterialization(
  materialization: ThreadRuntimeMaterialization,
): materialization is Extract<ThreadRuntimeMaterialization, "authoritative_partial" | "authoritative"> {
  return materialization === "authoritative_partial" || materialization === "authoritative"
}

export type ThreadSyncReplicaQuality = "best_effort" | "continuity_validated"

export function resolveCapturedRuntimeMetadata(args: {
  currentMaterialization: ThreadRuntimeMaterialization
  explicitMaterialization?: Exclude<ThreadRuntimeMaterialization, "empty">
  currentRuntimeConnectionVersion: number | null
  connectionVersion: number
}): {
  materialization: ThreadRuntimeMaterialization
  runtimeConnectionVersion: number | null
} {
  const materialization =
    args.explicitMaterialization ??
    (args.currentMaterialization === "empty" ? "control_plane_only" : args.currentMaterialization)

  if (args.explicitMaterialization) {
    return {
      materialization,
      runtimeConnectionVersion:
        materialization === "authoritative" || materialization === "authoritative_partial"
          ? args.connectionVersion
          : null,
    }
  }

  if (materialization === "authoritative" || materialization === "authoritative_partial") {
    return {
      materialization,
      runtimeConnectionVersion: args.currentRuntimeConnectionVersion ?? args.connectionVersion,
    }
  }

  return {
    materialization,
    runtimeConnectionVersion: null,
  }
}


function buildControlPlaneOnlyRuntimeStatus(args: {
  controlPlaneAgentType: AgentType | null
  currentSnapshot: AgentRuntimeSnapshot
  sessionStatus: string
  controlPlaneDevServerError: string | null
  codeServerError: string | null
}): AgentRuntimeStatus {
  const agentType = args.controlPlaneAgentType ?? args.currentSnapshot.capabilities.agentType
  const phase: AgentRuntimeStatus["phase"] =
    args.sessionStatus === "error"
      ? "error"
      : args.sessionStatus === "running"
        ? "running"
        : "idle"

  return {
    phase,
    agentType,
    startedAt: null,
    finishedAt: null,
    finishReason: null,
    error:
      phase === "error"
        ? {
            type: "runtime_error",
            message: args.codeServerError ?? args.controlPlaneDevServerError ?? "Runtime error",
          }
        : null,
  }
}

export function buildControlPlaneOnlyRuntimeSnapshot(args: {
  currentSnapshot: AgentRuntimeSnapshot
  controlPlaneAgentType: AgentType | null
  sessionStatus: string
  controlPlaneDevServerError: string | null
  codeServerError: string | null
  controlPlaneConversationEpoch: number
  runtimeSnapshot: ThreadControlPlaneRuntimeSnapshotLike | null
}): AgentRuntimeSnapshot {
  const runtimeSnapshot = args.runtimeSnapshot
  const status =
    runtimeSnapshot?.status ??
    buildControlPlaneOnlyRuntimeStatus({
      controlPlaneAgentType: args.controlPlaneAgentType,
      currentSnapshot: args.currentSnapshot,
      sessionStatus: args.sessionStatus,
      controlPlaneDevServerError: args.controlPlaneDevServerError,
      codeServerError: args.codeServerError,
    })
  const controlPlaneOnlyAvailability: AgentRuntimeActionAvailabilityMap = {
    abort: status.phase === "running"
      ? args.currentSnapshot.actions.availability.abort
      : {
          available: true,
          enabled: false,
          disabledReason: "No active run",
        },
    reply_permission: args.currentSnapshot.actions.availability.reply_permission,
    reply_question: args.currentSnapshot.actions.availability.reply_question,
    question_reject: args.currentSnapshot.actions.availability.question_reject,
  }

  return {
    timeline: [],
    pendingMessages: [],
    pendingInteractions: runtimeSnapshot?.pendingInteractions ?? [],
    status,
    usage: {},
    actions: {
      availability: controlPlaneOnlyAvailability,
      revertMessageId: null,
    },
    activeEditOperation: null,
    conversationEpoch: runtimeSnapshot?.conversationEpoch ?? args.controlPlaneConversationEpoch,
    sessionActionOutcome: null,
    capabilities: args.currentSnapshot.capabilities,
    cursorState: {
      providerEventId: null,
      canonicalEventId: null,
      providerSyncBoundaryId: null,
    },
  }
}
